import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import CustomEditor from '../../../admin-panel/custom/CustomEditor';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Form, Button, Card, Row, Col, InputGroup, Spinner } from 'react-bootstrap';

const FRONTEND_BASE_URL = 'https://mangoloops.com';

// Utility helpers for auto generating metadata from URL & parameters
const toTitleCase = (str) => {
    if (!str) return '';
    return str
        .replace(/[\-_]/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
};

const parseSlugInfo = (urlPath) => {
    if (!urlPath) return { fromCity: '', toCity: '', slugTitle: '', fullSlugTitle: '' };
    const clean = urlPath.trim().replace(/^\/+|\/+$/g, '');
    if (!clean) return { fromCity: '', toCity: '', slugTitle: '', fullSlugTitle: '' };

    const parts = clean.split('/');
    const lastPart = parts[parts.length - 1] || '';

    // Check if route format like 'mumbai-to-pune' or 'mumbai-to-pune-cab'
    const routeMatch = lastPart.match(/^([a-zA-Z0-9]+)-to-([a-zA-Z0-9]+)/i);
    let fromCity = '';
    let toCity = '';
    if (routeMatch) {
        fromCity = toTitleCase(routeMatch[1]);
        toCity = toTitleCase(routeMatch[2]);
    }

    const cleanTitle = lastPart.replace(/-(cab|cabs|taxi|taxis|tour|tours|package|packages|hotel|hotels|yatra|yatras|booking|rentals)/gi, '');
    const slugTitle = toTitleCase(cleanTitle);

    return {
        fromCity,
        toCity,
        slugTitle,
        fullSlugTitle: toTitleCase(lastPart)
    };
};

const generateSeoMetadataFromState = (data, options = { records: [] }) => {
    const {
        custom_url,
        existing_page_url,
        module,
        linked_type,
        linked_record_id,
        city_param,
        search_query_param,
        from_city_param,
        to_city_param
    } = data;

    const urlToParse = (linked_type === 'existing_page' && existing_page_url) ? existing_page_url : custom_url;
    const slugInfo = parseSlugInfo(urlToParse);

    let city = (city_param || '').trim();
    let fromCity = (from_city_param || slugInfo.fromCity || '').trim();
    let toCity = (to_city_param || slugInfo.toCity || '').trim();
    let searchQuery = (search_query_param || '').trim();

    if (!city && !fromCity && slugInfo.slugTitle && !fromCity && !toCity) {
        city = slugInfo.slugTitle;
    }

    let recordLabel = '';
    if (linked_type === 'record' && linked_record_id && options.records) {
        const foundRec = options.records.find(r => String(r.value) === String(linked_record_id));
        if (foundRec) recordLabel = foundRec.label;
    }

    const moduleNames = {
        holidays: { full: 'Holiday Tour Packages', short: 'Holiday Packages' },
        hotels: { full: 'Hotels & Stay Packages', short: 'Hotels' },
        cabs: { full: 'Cab & Taxi Service', short: 'Cabs' },
        yatras: { full: 'Tirth Yatra Packages', short: 'Tirth Yatra' },
        medical_tourism: { full: 'Medical Tourism & Treatment', short: 'Medical Tourism' }
    };

    const modInfo = moduleNames[module] || { full: 'Travel Packages', short: 'Travel' };

    let generatedTitle = '';
    let generatedDesc = '';
    let generatedKeywords = [];

    if (module === 'cabs' || (fromCity && toCity)) {
        const routeStr = (fromCity && toCity) ? `${fromCity} to ${toCity}` : (city || slugInfo.fullSlugTitle || 'Outstation');
        generatedTitle = `Best ${routeStr} Cab & Taxi Service | Low Fares | MangoLoops`;
        generatedDesc = `Book hassle-free ${routeStr} cabs & outstation taxis at guaranteed best prices. Verified drivers, instant booking & 24/7 travel support on MangoLoops.`;
        generatedKeywords = [
            `${routeStr} cab`,
            `${routeStr} taxi`,
            `book cab ${routeStr}`,
            `outstation cab ${routeStr}`,
            `cab rental ${routeStr}`,
            'mangoloops cabs'
        ];
    } else if (recordLabel) {
        generatedTitle = `${recordLabel} | ${modInfo.full} | MangoLoops`;
        generatedDesc = `Explore and book ${recordLabel} on MangoLoops. Exclusive deals on stay, sightseeing, transfers & 24/7 customer support.`;
        generatedKeywords = [
            recordLabel.toLowerCase(),
            `${modInfo.short.toLowerCase()} packages`,
            'book tour online',
            'mangoloops'
        ];
    } else if (city && searchQuery) {
        generatedTitle = `Best ${city} ${toTitleCase(searchQuery)} Packages | MangoLoops`;
        generatedDesc = `Explore top-rated ${searchQuery} tour packages in ${city}. Exclusive deals on hotel stay, sightseeing & transfers on MangoLoops.`;
        generatedKeywords = [
            `${city.toLowerCase()} ${searchQuery.toLowerCase()}`,
            `${city.toLowerCase()} tour packages`,
            `${searchQuery.toLowerCase()} in ${city.toLowerCase()}`,
            'mangoloops holidays'
        ];
    } else if (city && fromCity) {
        generatedTitle = `Best ${city} Tour Packages from ${fromCity} | MangoLoops`;
        generatedDesc = `Explore top-rated ${city} holiday tour packages from ${fromCity} on MangoLoops. Exclusive deals on hotels, sightseeing, activities & transfers.`;
        generatedKeywords = [
            `${city.toLowerCase()} tour packages`,
            `${fromCity.toLowerCase()} to ${city.toLowerCase()} trip`,
            `${city.toLowerCase()} holiday packages`,
            'mangoloops'
        ];
    } else if (city) {
        generatedTitle = `Best ${city} ${modInfo.full} & Holiday Deals | MangoLoops`;
        generatedDesc = `Plan your trip to ${city}! Explore top-rated ${modInfo.short.toLowerCase()}, customized travel itineraries, and exclusive deals on MangoLoops.`;
        generatedKeywords = [
            `${city.toLowerCase()} ${modInfo.short.toLowerCase()}`,
            `${city.toLowerCase()} tour packages`,
            `best trip to ${city.toLowerCase()}`,
            `places to visit in ${city.toLowerCase()}`,
            'mangoloops'
        ];
    } else if (searchQuery) {
        generatedTitle = `Best ${toTitleCase(searchQuery)} ${modInfo.full} | MangoLoops`;
        generatedDesc = `Discover and book top-rated ${searchQuery} ${modInfo.short.toLowerCase()} with exclusive deals and 24/7 support on MangoLoops.`;
        generatedKeywords = [
            `${searchQuery.toLowerCase()} packages`,
            `${searchQuery.toLowerCase()} ${modInfo.short.toLowerCase()}`,
            'mangoloops travel'
        ];
    } else if (slugInfo.fullSlugTitle) {
        generatedTitle = `Best ${slugInfo.fullSlugTitle} | MangoLoops`;
        generatedDesc = `Discover top-rated ${slugInfo.fullSlugTitle} on MangoLoops. Explore exclusive travel deals, customized itineraries & 24/7 support.`;
        generatedKeywords = [
            slugInfo.fullSlugTitle.toLowerCase(),
            `${modInfo.short.toLowerCase()} deals`,
            'mangoloops'
        ];
    } else {
        generatedTitle = `Explore ${modInfo.full} | MangoLoops`;
        generatedDesc = `Discover and book top-rated ${modInfo.full.toLowerCase()} with exclusive deals, transfers & 24/7 travel support on MangoLoops.`;
        generatedKeywords = [
            `${modInfo.short.toLowerCase()} packages`,
            'travel deals',
            'mangoloops'
        ];
    }

    const keywordsStr = Array.from(new Set(generatedKeywords)).join(', ');

    return {
        meta_title: generatedTitle,
        meta_description: generatedDesc,
        meta_keywords: keywordsStr,
        og_title: generatedTitle,
        og_description: generatedDesc,
        og_image: data.og_image || `${FRONTEND_BASE_URL}/uploads/banner.jpg`
    };
};

const FormCustomSeoUrl = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [urlValidation, setUrlValidation] = useState({ checking: false, valid: null, message: '' });
    const [moduleOptions, setModuleOptions] = useState({ cities: [], records: [], routes: [] });
    const [optionsLoading, setOptionsLoading] = useState(false);

    const [formData, setFormData] = useState({
        custom_url: '',
        module: 'holidays',
        linked_type: 'filter', // 'filter', 'record', 'existing_page'
        linked_record_id: '',
        existing_page_url: '',
        city_param: '',
        search_query_param: '',
        from_city_param: '',
        to_city_param: '',
        category_param: '',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        og_title: '',
        og_description: '',
        og_image: '',
        custom_html_content: '',
        content_position: 'above',
        status: 'active'
    });

    const fetchModuleOptions = useCallback(async (moduleName) => {
        setOptionsLoading(true);
        try {
            const response = await superAdminApiClient.get(`/superadmin/custom-seo-urls/module-options?module=${moduleName}`);
            if (response.data.success) {
                setModuleOptions(response.data.data || { cities: [], records: [], routes: [] });
            }
        } catch (error) {
            console.error('Error fetching module options:', error);
        } finally {
            setOptionsLoading(false);
        }
    }, []);

    const fetchDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await superAdminApiClient.get(`/superadmin/custom-seo-urls/${id}`);
            if (response.data.success) {
                const data = response.data.data;
                const qParams = data.query_params || {};

                setFormData({
                    custom_url: data.custom_url || '',
                    module: data.module || 'holidays',
                    linked_type: data.linked_type || 'filter',
                    linked_record_id: data.linked_record_id || '',
                    existing_page_url: qParams.target_url || qParams.existing_page_url || '',
                    city_param: qParams.city || '',
                    search_query_param: qParams.searchQuery || qParams.q || '',
                    from_city_param: qParams.from_city || '',
                    to_city_param: qParams.to_city || '',
                    category_param: qParams.category || '',
                    meta_title: data.meta_title || '',
                    meta_description: data.meta_description || '',
                    meta_keywords: data.meta_keywords || '',
                    og_title: data.og_title || '',
                    og_description: data.og_description || '',
                    og_image: data.og_image || '',
                    custom_html_content: data.custom_html_content || '',
                    content_position: data.content_position || 'above',
                    status: data.status || 'active'
                });

                fetchModuleOptions(data.module || 'holidays');
            } else {
                toast.error('Failed to load SEO URL details');
                navigate('/superadmin/seo/custom-urls');
            }
        } catch (error) {
            console.error('Error fetching details:', error);
            toast.error('Error loading Custom SEO URL');
            navigate('/superadmin/seo/custom-urls');
        } finally {
            setLoading(false);
        }
    }, [id, navigate, fetchModuleOptions]);

    useEffect(() => {
        if (isEdit) {
            fetchDetails();
        } else {
            fetchModuleOptions('holidays');
        }
    }, [isEdit, fetchDetails, fetchModuleOptions]);

    const handleAutoGenerateSeo = useCallback((showToast = true) => {
        const generated = generateSeoMetadataFromState(formData, moduleOptions);
        setFormData(prev => ({
            ...prev,
            meta_title: generated.meta_title,
            meta_description: generated.meta_description,
            meta_keywords: generated.meta_keywords,
            og_title: generated.og_title,
            og_description: generated.og_description,
            og_image: generated.og_image
        }));
        if (showToast) {
            toast.success('SEO & Open Graph metadata auto-populated from URL & parameters!');
        }
    }, [formData, moduleOptions]);

    // Auto-populate blank SEO fields when URL or parameters change (for new records or when title is blank)
    useEffect(() => {
        if (!isEdit && (!formData.meta_title || formData.meta_title.trim() === '')) {
            if (formData.custom_url || formData.city_param || formData.from_city_param || formData.to_city_param || formData.search_query_param || formData.linked_record_id) {
                const generated = generateSeoMetadataFromState(formData, moduleOptions);
                setFormData(prev => ({
                    ...prev,
                    meta_title: prev.meta_title || generated.meta_title,
                    meta_description: prev.meta_description || generated.meta_description,
                    meta_keywords: prev.meta_keywords || generated.meta_keywords,
                    og_title: prev.og_title || generated.og_title,
                    og_description: prev.og_description || generated.og_description,
                    og_image: prev.og_image || generated.og_image
                }));
            }
        }
    }, [
        formData.custom_url,
        formData.city_param,
        formData.from_city_param,
        formData.to_city_param,
        formData.search_query_param,
        formData.linked_record_id,
        formData.module,
        formData.linked_type,
        formData.meta_title,
        isEdit,
        moduleOptions
    ]);

    const handleExistingPageUrlChange = (e) => {
        const rawUrl = e.target.value;

        setFormData(prev => {
            const nextState = {
                ...prev,
                existing_page_url: rawUrl
            };

            if (rawUrl) {
                try {
                    let urlObj;
                    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
                        urlObj = new URL(rawUrl);
                    } else {
                        const dummyBase = 'https://mangoloops.com';
                        const pathWithSlash = rawUrl.startsWith('/') ? rawUrl : '/' + rawUrl;
                        urlObj = new URL(dummyBase + pathWithSlash);
                    }

                    const searchParams = urlObj.searchParams;
                    const pathname = urlObj.pathname.toLowerCase();

                    // Auto detect module if pathname indicates it
                    if (pathname.includes('/cab')) nextState.module = 'cabs';
                    else if (pathname.includes('/hotel')) nextState.module = 'hotels';
                    else if (pathname.includes('/yatra') || pathname.includes('/tirth')) nextState.module = 'yatras';
                    else if (pathname.includes('/medical')) nextState.module = 'medical_tourism';
                    else if (pathname.includes('/holiday')) nextState.module = 'holidays';

                    if (searchParams.get('city')) nextState.city_param = searchParams.get('city');
                    if (searchParams.get('searchQuery') || searchParams.get('q')) {
                        nextState.search_query_param = searchParams.get('searchQuery') || searchParams.get('q');
                    }
                    if (searchParams.get('from_city')) nextState.from_city_param = searchParams.get('from_city');
                    if (searchParams.get('to_city')) nextState.to_city_param = searchParams.get('to_city');
                    if (searchParams.get('category')) nextState.category_param = searchParams.get('category');
                } catch (err) {
                    // Ignore parse errors while typing partial input
                }
            }

            return nextState;
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'custom_url') {
            setUrlValidation({ checking: false, valid: null, message: '' });
        }
    };

    const handleModuleChange = (e) => {
        const newModule = e.target.value;
        setFormData(prev => ({
            ...prev,
            module: newModule,
            linked_type: 'filter',
            linked_record_id: '',
            existing_page_url: '',
            city_param: '',
            search_query_param: '',
            from_city_param: '',
            to_city_param: '',
            category_param: ''
        }));
        fetchModuleOptions(newModule);
    };

    const handleContentChange = (contentHtml) => {
        setFormData(prev => ({
            ...prev,
            custom_html_content: contentHtml
        }));
    };

    const validateCustomUrl = async () => {
        if (!formData.custom_url.trim()) {
            setUrlValidation({ checking: false, valid: false, message: 'URL path is required' });
            return;
        }

        setUrlValidation({ checking: true, valid: null, message: 'Checking availability...' });
        try {
            const response = await superAdminApiClient.post('/superadmin/custom-seo-urls/validate-url', {
                custom_url: formData.custom_url.trim(),
                id: isEdit ? id : null
            });

            if (response.data.valid) {
                setUrlValidation({ checking: false, valid: true, message: 'URL is available & valid!' });
                if (response.data.normalizedUrl) {
                    setFormData(prev => ({ ...prev, custom_url: response.data.normalizedUrl }));
                }
            } else {
                setUrlValidation({ checking: false, valid: false, message: response.data.message || 'Invalid or conflicting URL' });
            }
        } catch (error) {
            setUrlValidation({ checking: false, valid: false, message: 'Validation request failed' });
        }
    };

    const handlePresetRouteSelect = (routeVal) => {
        const found = moduleOptions.routes.find(r => r.value === routeVal);
        if (found) {
            setFormData(prev => ({
                ...prev,
                from_city_param: found.from,
                to_city_param: found.to,
                city_param: found.from
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic Front Validation
        if (!formData.custom_url.trim()) {
            toast.error('Please enter a valid Custom URL path');
            return;
        }
        if (formData.linked_type === 'existing_page' && !formData.existing_page_url.trim()) {
            toast.error('Please enter the Existing Page URL');
            return;
        }
        if (!formData.meta_title.trim()) {
            toast.error('Meta Title is required');
            return;
        }
        if (!formData.meta_description.trim()) {
            toast.error('Meta Description is required');
            return;
        }

        setSubmitting(true);

        try {
            // Build query_params object based on module selection
            const query_params = {};
            if (formData.city_param.trim()) query_params.city = formData.city_param.trim();
            if (formData.search_query_param.trim()) {
                query_params.searchQuery = formData.search_query_param.trim();
                query_params.q = formData.search_query_param.trim();
            }
            if (formData.from_city_param.trim()) query_params.from_city = formData.from_city_param.trim();
            if (formData.to_city_param.trim()) query_params.to_city = formData.to_city_param.trim();
            if (formData.category_param.trim()) query_params.category = formData.category_param.trim();

            if (formData.linked_type === 'existing_page' && formData.existing_page_url.trim()) {
                let raw = formData.existing_page_url.trim();
                if (!raw.startsWith('/') && !raw.startsWith('http')) {
                    raw = '/' + raw;
                }
                query_params.target_url = raw;
                query_params.existing_page_url = raw;

                // Also parse any query parameters from existing_page_url into query_params
                try {
                    const dummyBase = 'https://mangoloops.com';
                    const urlObj = new URL(raw.startsWith('http') ? raw : dummyBase + raw);
                    urlObj.searchParams.forEach((val, key) => {
                        if (val && !query_params[key]) {
                            query_params[key] = val;
                        }
                    });
                } catch (e) {
                    // Ignore
                }
            }

            const payload = {
                custom_url: formData.custom_url.trim(),
                module: formData.module,
                linked_type: formData.linked_type,
                linked_record_id: formData.linked_record_id || null,
                query_params,
                meta_title: formData.meta_title.trim(),
                meta_description: formData.meta_description.trim(),
                meta_keywords: formData.meta_keywords ? formData.meta_keywords.trim() : '',
                og_title: formData.og_title ? formData.og_title.trim() : formData.meta_title.trim(),
                og_description: formData.og_description ? formData.og_description.trim() : formData.meta_description.trim(),
                og_image: formData.og_image ? formData.og_image.trim() : '',
                custom_html_content: formData.custom_html_content,
                content_position: formData.content_position,
                status: formData.status
            };

            let response;
            if (isEdit) {
                response = await superAdminApiClient.put(`/superadmin/custom-seo-urls/${id}`, payload);
            } else {
                response = await superAdminApiClient.post('/superadmin/custom-seo-urls', payload);
            }

            if (response.data.success) {
                const msg = isEdit ? 'Custom SEO URL updated successfully!' : 'Custom SEO URL created successfully!';
                navigate('/superadmin/seo/custom-urls', {
                    replace: true,
                    state: { successMessage: msg }
                });
            }
        } catch (error) {
            console.error('Error saving Custom SEO URL:', error);
            const msg = error.response?.data?.message || 'Failed to save Custom SEO URL';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="page-content text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading Custom SEO URL Form...</p>
            </div>
        );
    }

    const previewFullUrl = `${FRONTEND_BASE_URL}${formData.custom_url.startsWith('/') ? formData.custom_url : '/' + formData.custom_url}`;

    return (
        <div className="page-content">
            <div className="container-fluid">
                {/* Header Title */}
                <div className="row mb-3">
                    <div className="col-12 d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                            <h4 className="mb-1 text-dark fw-bold">{isEdit ? 'Edit Custom SEO URL' : 'Create Custom SEO URL'}</h4>
                            <p className="text-muted mb-0">Configure SEO landing URL path, metadata, content & linked listing data</p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {formData.custom_url && (
                                <a
                                    href={`${formData.custom_url}?preview=true`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-info d-inline-flex align-items-center gap-1"
                                >
                                    <i className="ri-eye-line"></i> Preview Landing Page
                                </a>
                            )}
                            <Link to="/superadmin/seo/custom-urls" className="btn btn-outline-secondary">
                                <i className="ri-arrow-left-line me-1"></i> Back to List
                            </Link>
                        </div>
                    </div>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Row className="g-4">
                        {/* Left Main Form */}
                        <Col lg={8}>
                            {/* Card 1: URL & Linked Page */}
                            <Card className="border shadow-sm mb-4">
                                <Card.Header className="bg-light border-bottom">
                                    <h5 className="mb-0 text-primary fs-15 fw-bold">
                                        <i className="ri-link-m me-1"></i> 1. Custom URL Path & Linked Module Data
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    {/* URL Input */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">
                                            Custom URL Path <span className="text-danger">*</span>
                                        </Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text className="bg-light">{FRONTEND_BASE_URL}</InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                name="custom_url"
                                                placeholder="/holidays/goa"
                                                value={formData.custom_url}
                                                onChange={handleInputChange}
                                                onBlur={validateCustomUrl}
                                                required
                                            />
                                            <Button type="button" variant="outline-primary" onClick={validateCustomUrl} disabled={urlValidation.checking}>
                                                {urlValidation.checking ? <Spinner size="sm" animation="border" /> : 'Validate'}
                                            </Button>
                                        </InputGroup>
                                        <Form.Text className="text-muted">
                                            Must start with <code>/</code> (e.g., <code>/holidays/goa</code>, <code>/goa-tour-packages-from-mumbai</code>, <code>/cab/mumbai-to-pune</code>).
                                        </Form.Text>

                                        {/* URL Validation Message */}
                                        {urlValidation.valid === true && (
                                            <div className="text-success small mt-1 fw-semibold">
                                                <i className="ri-checkbox-circle-fill me-1"></i> {urlValidation.message}
                                            </div>
                                        )}
                                        {urlValidation.valid === false && (
                                            <div className="text-danger small mt-1 fw-semibold">
                                                <i className="ri-error-warning-fill me-1"></i> {urlValidation.message}
                                            </div>
                                        )}

                                        {/* URL Preview Box */}
                                        {formData.custom_url && (
                                            <div className="p-2 bg-light border rounded mt-2 small">
                                                <span className="text-muted me-1">Live URL Preview:</span>
                                                <a href={previewFullUrl} target="_blank" rel="noopener noreferrer" className="fw-bold text-primary">
                                                    {previewFullUrl}
                                                </a>
                                            </div>
                                        )}
                                    </Form.Group>

                                    <Row className="g-3">
                                        {/* Module Selection */}
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">
                                                    Target Module <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Select
                                                    name="module"
                                                    value={formData.module}
                                                    onChange={handleModuleChange}
                                                    required
                                                >
                                                    <option value="holidays">Holidays</option>
                                                    <option value="hotels">Hotels</option>
                                                    <option value="cabs">Cabs</option>
                                                    <option value="yatras">Tirth Yatra</option>
                                                    <option value="medical_tourism">Medical Tourism</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                         {/* Link Type */}
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">Linking Mode</Form.Label>
                                                <Form.Select
                                                    name="linked_type"
                                                    value={formData.linked_type}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="filter">Filter Criteria / City / Search</option>
                                                    <option value="record">Specific Package / Item Listing</option>
                                                    <option value="existing_page">Existing Page URL / Internal Link</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {/* Existing Page URL Input if linked_type === 'existing_page' */}
                                    {formData.linked_type === 'existing_page' && (
                                        <Card className="bg-light-subtle border p-3 mb-3 rounded shadow-sm">
                                            <Form.Group className="mb-2">
                                                <Form.Label className="fw-semibold">
                                                    Existing Page URL / Path <span className="text-danger">*</span>
                                                </Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text className="bg-light">{FRONTEND_BASE_URL}</InputGroup.Text>
                                                    <Form.Control
                                                        type="text"
                                                        name="existing_page_url"
                                                        placeholder="/holidays-list?city=Goa&searchQuery=Honeymoon or /cabs/search?from_city=Mumbai&to_city=Pune"
                                                        value={formData.existing_page_url}
                                                        onChange={handleExistingPageUrlChange}
                                                        required={formData.linked_type === 'existing_page'}
                                                    />
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Paste or type an existing page URL (e.g. <code>/holidays-list?city=Goa&amp;searchQuery=Honeymoon</code>). The custom SEO page will automatically render data matching this existing page.
                                                </Form.Text>
                                            </Form.Group>

                                            {(formData.city_param || formData.from_city_param || formData.search_query_param) && (
                                                <div className="mt-2 p-2 bg-white border rounded fs-12">
                                                    <span className="fw-bold text-success me-2">
                                                        <i className="ri-checkbox-circle-line me-1"></i>Extracted Parameters:
                                                    </span>
                                                    {formData.city_param && <span className="badge bg-light text-dark border me-1">City: {formData.city_param}</span>}
                                                    {formData.from_city_param && formData.to_city_param && <span className="badge bg-light text-dark border me-1">Route: {formData.from_city_param} → {formData.to_city_param}</span>}
                                                    {formData.search_query_param && <span className="badge bg-light text-dark border me-1">Search: {formData.search_query_param}</span>}
                                                    {formData.category_param && <span className="badge bg-light text-dark border me-1">Category: {formData.category_param}</span>}
                                                </div>
                                            )}
                                        </Card>
                                    )}

                                    {/* Specific Record Selection if linked_type === 'record' */}
                                    {formData.linked_type === 'record' && (
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold d-flex align-items-center justify-content-between flex-wrap">
                                                <span>Select Specific Listing Item <span className="text-danger">*</span></span>
                                                <span className="text-muted fw-normal fs-12">
                                                    <i className="ri-search-line me-1"></i>Type to search by package name or city
                                                </span>
                                            </Form.Label>
                                            {optionsLoading ? (
                                                <div className="text-muted small">Loading items...</div>
                                            ) : (
                                                <Select
                                                    options={moduleOptions.records}
                                                    value={moduleOptions.records.find(r => String(r.value) === String(formData.linked_record_id)) || null}
                                                    onChange={(selected) => {
                                                        const newVal = selected ? selected.value : '';
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            linked_record_id: newVal
                                                        }));
                                                    }}
                                                    placeholder="-- Type to search & select specific package / item --"
                                                    isClearable
                                                    isSearchable
                                                    noOptionsMessage={() => "No matching packages or items found"}
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            borderColor: '#ced4da',
                                                            minHeight: '38px',
                                                            borderRadius: '0.375rem',
                                                            boxShadow: 'none',
                                                            '&:hover': {
                                                                borderColor: '#86b7fe'
                                                            }
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            )}
                                        </Form.Group>
                                    )}

                                    {/* Filter Criteria Options */}
                                    <Card className="bg-light border-0 p-3 mb-2">
                                        <h6 className="fw-bold text-dark mb-2">Module Filter Parameters</h6>
                                        <p className="text-muted small mb-3">
                                            The custom URL will display listings matching these criteria from existing MangoLoops data.
                                        </p>

                                        <Row className="g-3">
                                            {/* City Selection */}
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">Target City / Destination</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="city_param"
                                                        placeholder="e.g. Goa, Manali, Kashmir"
                                                        value={formData.city_param}
                                                        onChange={handleInputChange}
                                                        list="city-options-list"
                                                    />
                                                    <datalist id="city-options-list">
                                                        {moduleOptions.cities.map((c, idx) => (
                                                            <option key={idx} value={c.value} />
                                                        ))}
                                                    </datalist>
                                                </Form.Group>
                                            </Col>

                                            {/* Search Query / Keyword */}
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">Search Keyword / Package Filter</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="search_query_param"
                                                        placeholder="e.g. Group Tour, Honeymoon"
                                                        value={formData.search_query_param}
                                                        onChange={handleInputChange}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            {/* Extra route options for Cabs */}
                                            {formData.module === 'cabs' && (
                                                <>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold">From City (Cab Pickup)</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="from_city_param"
                                                                placeholder="e.g. Mumbai"
                                                                value={formData.from_city_param}
                                                                onChange={handleInputChange}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold">To City (Cab Destination)</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="to_city_param"
                                                                placeholder="e.g. Pune"
                                                                value={formData.to_city_param}
                                                                onChange={handleInputChange}
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    {/* Preset Routes */}
                                                    {moduleOptions.routes && moduleOptions.routes.length > 0 && (
                                                        <Col md={12}>
                                                            <div className="d-flex align-items-center gap-1 flex-wrap">
                                                                <span className="small text-muted me-1">Popular Cab Routes:</span>
                                                                {moduleOptions.routes.map((r, i) => (
                                                                    <Button
                                                                        key={i}
                                                                        type="button"
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        className="py-0 px-2 fs-11"
                                                                        onClick={() => handlePresetRouteSelect(r.value)}
                                                                    >
                                                                        {r.label}
                                                                    </Button>
                                                                ))}
                                                            </div>
                                                        </Col>
                                                    )}
                                                </>
                                            )}
                                        </Row>
                                    </Card>
                                </Card.Body>
                            </Card>

                            {/* Card 2: SEO Metadata */}
                            <Card className="border shadow-sm mb-4">
                                <Card.Header className="bg-light border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 py-2">
                                    <h5 className="mb-0 text-primary fs-15 fw-bold">
                                        <i className="ri-search-eye-line me-1"></i> 2. SEO & Open Graph Metadata
                                    </h5>
                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        size="sm"
                                        className="d-inline-flex align-items-center gap-1 shadow-sm"
                                        onClick={() => handleAutoGenerateSeo(true)}
                                        title="Auto-fill Meta Title, Description, Keywords & OG tags based on URL and module parameters"
                                    >
                                        <i className="ri-magic-line"></i> ⚡ Auto-Generate Metadata
                                    </Button>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">
                                            Meta Title <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="meta_title"
                                            placeholder="e.g. Best Goa Tour Packages from Mumbai | MangoLoops"
                                            value={formData.meta_title}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <Form.Text className="text-muted">Recommended length: 50-60 characters</Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">
                                            Meta Description <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="meta_description"
                                            placeholder="e.g. Explore top-rated Goa holiday packages with exclusive deals on hotels, sightseeing & transfers."
                                            value={formData.meta_description}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <Form.Text className="text-muted">Recommended length: 150-160 characters</Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Meta Keywords</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="meta_keywords"
                                            placeholder="goa tour, goa packages, goa holidays, mumbai to goa trip"
                                            value={formData.meta_keywords}
                                            onChange={handleInputChange}
                                        />
                                        <Form.Text className="text-muted">Comma-separated keywords</Form.Text>
                                    </Form.Group>

                                    <hr className="my-3" />

                                    <h6 className="fw-bold text-dark mb-3">Open Graph (Social Sharing) Tags</h6>

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="small fw-semibold">OG Title</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="og_title"
                                                    placeholder="Defaults to Meta Title if blank"
                                                    value={formData.og_title}
                                                    onChange={handleInputChange}
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="small fw-semibold">OG Image URL</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="og_image"
                                                    placeholder="https://mangoloops.com/uploads/banner.jpg"
                                                    value={formData.og_image}
                                                    onChange={handleInputChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-2">
                                        <Form.Label className="small fw-semibold">OG Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            name="og_description"
                                            placeholder="Defaults to Meta Description if blank"
                                            value={formData.og_description}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                </Card.Body>
                            </Card>

                            {/* Card 3: Custom SEO Content Editor */}
                            <Card className="border shadow-sm mb-4">
                                <Card.Header className="bg-light border-bottom">
                                    <h5 className="mb-0 text-primary fs-15 fw-bold">
                                        <i className="ri-article-line me-1"></i> 3. Custom SEO Content (HTML / Rich Text)
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Content HTML / Rich Text</Form.Label>
                                        <CustomEditor
                                            data={formData.custom_html_content}
                                            onChange={handleContentChange}
                                            placeholder="Enter SEO headings, descriptive paragraphs, FAQ tables, or guide text..."
                                        />
                                        <Form.Text className="text-muted">
                                            This content will be rendered on the landing page at the configured position.
                                        </Form.Text>
                                    </Form.Group>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Right Sidebar Options */}
                        <Col lg={4}>
                            {/* Publishing Settings */}
                            <Card className="border shadow-sm mb-4">
                                <Card.Header className="bg-light border-bottom">
                                    <h5 className="mb-0 text-dark fs-15 fw-bold">Publishing & Position</h5>
                                </Card.Header>
                                <Card.Body>
                                    {/* Status Selection */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Page Status</Form.Label>
                                        <Form.Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                        >
                                            <option value="active">Active (Public & In Sitemap)</option>
                                            <option value="inactive">Inactive (Disabled / 404)</option>
                                            <option value="draft">Draft (Private)</option>
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Only <strong>Active</strong> URLs are accessible to public visitors and included in <code>sitemap.xml</code>.
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Content Position */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Custom Content Position</Form.Label>
                                        <div className="d-flex flex-column gap-2 border p-3 rounded bg-light">
                                            <Form.Check
                                                type="radio"
                                                id="pos-above"
                                                name="content_position"
                                                value="above"
                                                label="Above Listings (SEO Content → Listings)"
                                                checked={formData.content_position === 'above'}
                                                onChange={handleInputChange}
                                            />
                                            <Form.Check
                                                type="radio"
                                                id="pos-below"
                                                name="content_position"
                                                value="below"
                                                label="Below Listings (Listings → SEO Content)"
                                                checked={formData.content_position === 'below'}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </Form.Group>

                                    {/* Action Buttons */}
                                    <div className="d-grid gap-2 mt-4">
                                        <Button variant="primary" type="submit" size="lg" disabled={submitting}>
                                            {submitting ? (
                                                <>
                                                    <Spinner size="sm" animation="border" className="me-2" /> Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-save-line me-1"></i> {isEdit ? 'Update SEO URL' : 'Save Custom SEO URL'}
                                                </>
                                            )}
                                        </Button>

                                        <Link to="/superadmin/seo/custom-urls" className="btn btn-light border">
                                            Cancel
                                        </Link>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Info Guide Card */}
                            <Card className="border bg-light">
                                <Card.Body className="p-3">
                                    <h6 className="fw-bold text-dark mb-2">
                                        <i className="ri-information-line text-info me-1"></i> SEO URL Architecture
                                    </h6>
                                    <ul className="small text-muted ps-3 mb-0">
                                        <li className="mb-1">Does <strong>NOT</strong> duplicate packages or listings.</li>
                                        <li className="mb-1">References existing MangoLoops listing pages dynamically.</li>
                                        <li className="mb-1">Controls custom metadata, OG tags, canonical tag & SEO text block.</li>
                                        <li>Automatically updates <code>sitemap.xml</code> when saved as Active.</li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>
        </div>
    );
};

export default FormCustomSeoUrl;
